import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { timelineApi } from '../utils/timelineApi';
import LayoutHeader from '../layout/LayoutHeader';

const TimelineItem = styled.div`
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  padding: 16px;
  transition: all 0.2s ease;
  cursor: pointer;
  margin-bottom: 12px;
  
  &:hover {
    border-color: rgba(255, 255, 255, 0.3);
  }
`;

const DateGroupHeader = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: white;
  margin-bottom: 16px;
  margin-top: 24px;
  
  &:first-child {
    margin-top: 0;
  }
`;

const TimelineItemHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 8px;
`;

const TimelineItemType = styled.div`
  font-size: 10px;
  font-weight: 600;
  color: ${props => props.color || '#FF2300'};
  text-transform: uppercase;
  margin-bottom: 8px;
`;

const TimelineItemStatus = styled.span`
  font-size: 10px;
  color: rgba(255, 255, 255, 0.6);
  margin-left: 8px;
`;

const TimelineItemTitle = styled.div`
  font-size: 14px;
  font-weight: 500;
  color: white;
  margin-bottom: 4px;
`;

const TimelineItemDescription = styled.div`
  font-size: 12px;
  color: rgba(255, 255, 255, 0.7);
  line-height: 1.4;
  margin-bottom: 8px;
`;

const TimelineItemTime = styled.div`
  font-size: 11px;
  color: rgba(255, 255, 255, 0.5);
  margin-bottom: 12px;
  display: flex;
  align-items: center;
  gap: 4px;
`;

const TimelineItemAction = styled.button`
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: ${props => props.color || '#FF2300'};
  font-size: 11px;
  font-weight: 500;
  cursor: pointer;
  padding: 8px 12px;
  border-radius: 6px;
  width: 100%;
  transition: all 0.2s ease;
  
  &:hover {
    border-color: ${props => props.color || '#FF2300'};
    color: ${props => props.color || '#FF2300'};
  }
`;

const LoadingContainer = styled.div`
  color: white;
  text-align: center;
  padding: 60px 0;
  font-size: 12px;
`;

const EmptyState = styled.div`
  text-align: left;
  padding: 60px 0;
`;

const EmptyStateTitle = styled.h3`
  color: white;
  font-size: 12px;
  font-weight: 600;
  margin-bottom: 8px;
`;

const EmptyStateDescription = styled.p`
  color: rgba(255, 255, 255, 0.5);
  font-size: 12px;
  line-height: 1.5;
  margin-bottom: 24px;
  max-width: 300px;
`;

const EmptyStateButton = styled.button`
  background: #17A2B8;
  color: white;
  padding: 8px 20px;
  border-radius: 8px;
  font-size: 12px;
  border: none;
  cursor: pointer;
  transition: background-color 0.2s ease;
  
  &:hover {
    background: #20B2AA;
  }
`;

const SimpleEmptyState = styled.div`
  display: flex;
  align-items: center;
  padding: 20px 0;
`;

const EmptyStateText = styled.span`
  color: rgba(255, 255, 255, 0.5);
  font-size: 12px;
`;

const TabsContainer = styled.div`
  display: flex;
  width: 100%;
  justify-content: space-between;
  padding: 0 16px;
`;

const ActivityFeedPage = () => {
  const [timeline, setTimeline] = useState([]);
  const [groupedTimeline, setGroupedTimeline] = useState({});
  const [activeFilter, setActiveFilter] = useState('all');
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    loadTimeline();
  }, []);

  // Reload timeline when returning from settings page
  useEffect(() => {
    if (location.state?.fromSettings) {
      loadTimeline();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  const loadTimeline = async () => {
    try {
      const response = await timelineApi.getTimeline();
      
      if (response && response.success) {
        setTimeline(response.timeline || []);
        setGroupedTimeline(response.grouped || {});
      } else {
        setTimeline([]);
        setGroupedTimeline({});
      }
    } catch (error) {
      console.error('Failed to load timeline:', error);
      setTimeline([]);
      setGroupedTimeline({});
    }
  };

  const getFilteredTimeline = () => {
    if (!Array.isArray(timeline)) {
      return { timeline: [], grouped: {} };
    }
    
    let filtered = timeline;
    
    // Filter by type
    if (activeFilter !== 'all') {
      const filterMap = {
        'shows': ['show', 'ticket-update'],
        'releases': ['release'],
        'travel': ['flight', 'hotel', 'transport'],
        'tasks': ['task']
      };
      
      const typesToInclude = filterMap[activeFilter] || [];
      if (typesToInclude.length > 0) {
        filtered = filtered.filter(item => typesToInclude.includes(item.type));
      }
    }
    
    // Sort filtered items by date (earliest first) to ensure chronological order
    filtered = filtered.sort((a, b) => {
      const dateA = new Date(a.date);
      const dateB = new Date(b.date);
      return dateA - dateB;
    });
    
    // Group filtered items
    const grouped = {};
    filtered.forEach((item) => {
      const group = item.dateGroup || 'Other';
      if (!grouped[group]) {
        grouped[group] = [];
      }
      grouped[group].push(item);
    });
    
    // Sort items within each group by date (earliest first) to ensure chronological order
    Object.keys(grouped).forEach((group) => {
      grouped[group].sort((a, b) => {
        const dateA = new Date(a.date);
        const dateB = new Date(b.date);
        return dateA - dateB;
      });
    });
    
    return { timeline: filtered, grouped };
  };

  const getEmptyStateContent = () => {
    const emptyStates = {
      all: {
        title: 'No upcoming events',
        description: 'Add shows, releases, tasks, or travel to see them in your timeline'
      },
      shows: {
        text: 'No upcoming shows — add shows in Bookings to see them here'
      },
      releases: {
        text: 'No upcoming releases — add releases in Catalog to see them here'
      },
      travel: {
        text: 'No upcoming travel — add flights or hotels in Tour Manager to see them here'
      },
      tasks: {
        text: 'No upcoming to-dos — add tasks in Tasks/Flows to see them here'
      }
    };

    return emptyStates[activeFilter] || emptyStates.all;
  };

  const getTypeLabel = (type, subType) => {
    const labels = {
      'show': 'SHOW',
      'release': 'RELEASE',
      'task': 'TASK',
      'flight': 'FLIGHT',
      'hotel': 'HOTEL',
      'transport': 'TRANSPORT',
      'ticket-update': 'TICKET UPDATE'
    };
    
    return labels[type] || 'EVENT';
  };

  const getStatusLabel = (status, subType) => {
    if (status === 'confirmed') return 'Confirmed';
    if (status === 'pending') return 'Pending';
    if (status === 'due-soon') return 'Due Soon';
    if (status === 'overdue') return 'Overdue';
    if (status === 'update') return 'Update';
    return '';
  };

  const renderTimelineItem = (item) => {
    const itemColor = item.color || '#FF2300';
    const typeLabel = getTypeLabel(item.type, item.subType);
    const statusLabel = getStatusLabel(item.status, item.subType);
    
    return (
      <TimelineItem 
        key={item.id}
        onClick={() => item.actionUrl && navigate(item.actionUrl)}
      >
        <div style={{ flex: 1 }}>
          <TimelineItemType color={itemColor}>
            {typeLabel}
            {statusLabel && <TimelineItemStatus>• {statusLabel}</TimelineItemStatus>}
          </TimelineItemType>
          <TimelineItemTitle>{item.title}</TimelineItemTitle>
          <TimelineItemDescription>{item.description}</TimelineItemDescription>
          {item.timeContext && (
            <TimelineItemTime>
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span>{item.timeContext}</span>
            </TimelineItemTime>
          )}
          {item.actionLabel && (
            <TimelineItemAction 
              color={itemColor}
              onClick={(e) => {
                e.stopPropagation();
                if (item.actionUrl) navigate(item.actionUrl);
              }}
            >
              {item.actionLabel}
            </TimelineItemAction>
          )}
        </div>
      </TimelineItem>
    );
  };

  const getTabColor = (tabId) => {
    const tabColors = {
      all: '#FF2300',
      shows: '#FFFF00',
      releases: '#17A2B8',
      travel: '#FF9500',
      tasks: '#FF6B35'
    };
    return tabColors[tabId] || '#8E8E93';
  };

  const { timeline: filteredTimeline, grouped: filteredGrouped } = getFilteredTimeline();
  
  // Order date groups
  const dateGroupOrder = ['Today', 'Tomorrow', 'This Week', 'Next Week', 'Past', 'Overdue'];
  const orderedGroups = Object.keys(filteredGrouped).sort((a, b) => {
    const indexA = dateGroupOrder.indexOf(a);
    const indexB = dateGroupOrder.indexOf(b);
    if (indexA !== -1 && indexB !== -1) return indexA - indexB;
    if (indexA !== -1) return -1;
    if (indexB !== -1) return 1;
    return a.localeCompare(b);
  });

  return (
    <LayoutHeader>
      <div className="flex justify-center w-full">
        <div className="w-[390px] h-[80vh] flex flex-col items-center relative z-[0]">

          {/* Header */}
          <div className="px-4 my-[50px] flex w-full justify-between items-center">
            <span className="text-white text-[14px]" style={{ fontWeight: 300 }}>Timeline</span>
            <div className="flex items-center gap-4">
              <span 
                className="text-white text-[12px] font-normal cursor-pointer hover:opacity-70" 
                onClick={() => navigate('/timeline-settings', { state: { fromActivityFeed: true } })}
              >
                Settings
              </span>
              <span 
                className="text-white text-[12px] font-normal cursor-pointer hover:opacity-70" 
                onClick={() => {
                  const canGoBack = window.history.length > 1;
                  if (canGoBack) {
                    navigate(-1);
                  } else {
                    navigate('/');
                  }
                }}
              >
                Go back
              </span>
            </div>
          </div>

          {/* Tabs */}
          <div className="w-full mb-6">
            <TabsContainer>
              {[
                { id: 'all', label: 'All' },
                { id: 'shows', label: 'Shows' },
                { id: 'releases', label: 'Releases' },
                { id: 'travel', label: 'Touring' },
                { id: 'tasks', label: 'To Do' }
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveFilter(tab.id)}
                  className={`flex-1 px-4 py-3 text-[12px] font-normal border-b-2 transition-all ${
                    activeFilter === tab.id
                      ? 'text-white'
                      : 'text-[rgba(255,255,255,0.5)] border-transparent hover:text-white'
                  }`}
                  style={{
                    borderBottomColor: activeFilter === tab.id ? getTabColor(tab.id) : 'transparent'
                  }}
                >
                  {tab.label}
                </button>
              ))}
            </TabsContainer>
          </div>

          {/* Content */}
          <div className="w-full px-4 flex-1 overflow-y-auto">
            {filteredTimeline.length === 0 ? (
              activeFilter === 'all' ? (
                <EmptyState>
                  <EmptyStateTitle>{getEmptyStateContent().title}</EmptyStateTitle>
                  <EmptyStateDescription>{getEmptyStateContent().description}</EmptyStateDescription>
                </EmptyState>
              ) : (
                <SimpleEmptyState>
                  <EmptyStateText>{getEmptyStateContent().text}</EmptyStateText>
                </SimpleEmptyState>
              )
            ) : (
              <div>
                {orderedGroups.map((group) => (
                  <div key={group}>
                    <DateGroupHeader>{group}</DateGroupHeader>
                    {filteredGrouped[group].map(renderTimelineItem)}
                  </div>
                ))}
              </div>
            )}
          </div>


        </div>
      </div>
    </LayoutHeader>
  );
};

export default ActivityFeedPage; 